import { useEffect, useState } from 'react';
import { Plus, Search, Wallet } from 'lucide-react';
import api from '../lib/api';
import Header from '../components/Layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';

const OWNER_COLORS = {
  Raghu: 'bg-violet-50 text-violet-700',
  Jeet:  'bg-sky-50 text-sky-700',
};

const Contributions = () => {
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [dateFrom, setDateFrom]           = useState('');
  const [dateTo, setDateTo]               = useState('');
  const [dialogOpen, setDialogOpen]       = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0],
    owner: '',
    amount: '',
    description: '',
  });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/contributions');
      setContributions(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contributions', { ...form, amount: parseFloat(form.amount) });
      toast.success('Contribution added');
      setDialogOpen(false);
      setForm({ date: new Date().toISOString().split('T')[0], owner: '', amount: '', description: '' });
      load();
    } catch {
      toast.error('Failed to add contribution');
    }
  };

  const filtered = contributions.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.owner?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q);
    const matchFrom = !dateFrom || c.date >= dateFrom;
    const matchTo   = !dateTo   || c.date <= dateTo;
    return matchSearch && matchFrom && matchTo;
  });

  const byOwner = contributions.reduce((acc, c) => {
    acc[c.owner] = (acc[c.owner] || 0) + c.amount;
    return acc;
  }, {});

  const total = filtered.reduce((s, c) => s + c.amount, 0);

  const fmt = (n) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  const formatDate = (d) => {
    try { return format(new Date(d), 'dd MMM yyyy'); } catch { return d; }
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      <Header title="Owner Contributions" subtitle="Capital put in by Raghu & Jeet for Branofy expenses" />

      <div className="p-6 space-y-6">
        {/* Per-owner summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(byOwner).map(([owner, amt]) => (
            <div key={owner} className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center gap-4">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center font-bold text-lg ${OWNER_COLORS[owner] || 'bg-slate-100 text-slate-600'}`}>
                {owner[0]}
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">{owner}</p>
                <p className="text-xl font-bold text-slate-900">{fmt(amt)}</p>
                <p className="text-xs text-slate-400">
                  {contributions.filter(c => c.owner === owner).length} entries
                </p>
              </div>
            </div>
          ))}
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 bg-orange-50 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-orange-600" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs text-slate-500 font-medium">Total Contributed</p>
              <p className="text-xl font-bold text-slate-900">{fmt(Object.values(byOwner).reduce((s, v) => s + v, 0))}</p>
              <p className="text-xs text-slate-400">{contributions.length} entries</p>
            </div>
          </div>
        </div>

        {/* Filters + Add button */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search owner, description…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
            />
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="bg-white border-slate-200 w-40" />
            <span className="text-slate-400 text-sm">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="bg-white border-slate-200 w-40" />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="text-xs text-slate-400 hover:text-slate-600 underline">Clear</button>
            )}
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600 text-white active:scale-95">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Add Contribution
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Owner Contribution</DialogTitle></DialogHeader>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select value={form.owner} onValueChange={v => setForm({ ...form, owner: v })} required>
                    <SelectTrigger className="bg-slate-50"><SelectValue placeholder="Select owner" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Raghu">Raghu</SelectItem>
                      <SelectItem value="Jeet">Jeet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount (INR)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Vayah Vikas salary remainder" className="bg-slate-50" />
                </div>
                <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white">Add</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">No contributions found</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Owner</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((c, i) => (
                  <tr key={c.id || i} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap">{formatDate(c.date)}</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${OWNER_COLORS[c.owner] || 'bg-slate-100 text-slate-600'}`}>
                        {c.owner}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-slate-500">{c.description || '-'}</td>
                    <td className="px-5 py-3.5 text-right font-semibold text-violet-700 whitespace-nowrap">{fmt(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/60">
                  <td colSpan={3} className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase">
                    Total{(search || dateFrom || dateTo) ? ' (filtered)' : ''}
                  </td>
                  <td className="px-5 py-3 text-right font-bold text-slate-900">{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contributions;
